import React, { useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Globe, ExternalLink, Check } from 'lucide-react';
import { VALIDATION_STATUS, STATUS_META, DEFAULT_STATUS } from '../i18n/validationStatus';

// Portail de VALIDATION des traductions (7 langues cibles).
// But : permettre à Carlos de parcourir chaque page dans chaque langue, page par page,
// via des liens profonds `?lang=<code>` (lus au chargement par LanguageProvider).
// N'édite rien : c'est un HUB DE LIENS de vérification (l'éditeur de clés = /admin/translations).

// 7 langues cibles du mandat (fr + 6). 'zh-CN' est un alias de 'zh' (chinois simplifié).
const LANGS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'en', label: 'English', flag: '🇬🇧', rtl: false },
  { code: 'es', label: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'pt-BR', label: 'Português (BR)', flag: '🇧🇷', rtl: false },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'ar-SY', label: 'Arabe syrien', flag: '🇸🇾', rtl: true },
  { code: 'zh-CN', label: '简体中文', flag: '🇨🇳', rtl: false },
];

// Catalogue des routes groupées par audience. `param` => la route contient un identifiant :
// un champ de saisie permet de fournir une vraie valeur avant d'ouvrir le lien.
const GROUPS = [
  {
    title: 'Site public / vitrine (sans connexion)',
    routes: [
      { path: '/', label: 'Accueil / vitrine' },
      { path: '/remobiles', label: 'Vitrine RÉMOBILES' },
      { path: '/aide', label: 'Guide-moi (aide + vidéos)' },
      { path: '/tarifs', label: 'Tarifs publics' },
      { path: '/carte', label: 'Carte / nous trouver' },
      { path: '/offres-flash', label: 'Offres flash' },
      { path: '/politique-confidentialite', label: 'Politique de confidentialité' },
      { path: '/privacy', label: 'Privacy (EN)' },
      { path: '/conditions-utilisation', label: "Conditions d'utilisation" },
      { path: '/cgu', label: 'CGU' },
      { path: '/demande-facture', label: 'Demande de facture' },
      { path: '/track/:ref', label: 'Suivi de réparation', param: 'ref', sample: 'DEMO' },
      { path: '/suivi/:ref', label: 'Suivi (alias)', param: 'ref', sample: 'DEMO' },
      { path: '/shop/:shopId', label: 'Boutique', param: 'shopId', sample: 'demo' },
    ],
  },
  {
    title: 'Connexion & inscription',
    routes: [
      { path: '/login', label: 'Connexion' },
      { path: '/login/client', label: 'Connexion client' },
      { path: '/login/staff', label: 'Connexion staff' },
      { path: '/login/entreprise', label: 'Connexion entreprise (B2B)' },
      { path: '/inscription-client', label: 'Inscription client' },
      { path: '/inscription-b2b', label: 'Inscription B2B' },
      { path: '/reset-password', label: 'Réinitialiser le mot de passe' },
      { path: '/mot-de-passe-oublie', label: 'Mot de passe oublié' },
      { path: '/verifier-email', label: "Vérifier l'e-mail" },
    ],
  },
  {
    title: 'Parcours client',
    routes: [
      { path: '/espace-client', label: 'Espace client' },
      { path: '/espace-b2b', label: 'Espace B2B' },
      { path: '/clients/mots-de-passe', label: 'Mes mots de passe (client)' },
      { path: '/boutique/commandes', label: 'Mes commandes boutique' },
      { path: '/paiement-succes', label: 'Paiement réussi' },
      { path: '/acompte-devis-succes', label: 'Acompte devis réussi' },
      { path: '/devis/accepter/:devisId', label: 'Accepter un devis', param: 'devisId', sample: 'DEMO' },
      { path: '/devis/paiement/:devisId', label: 'Payer un devis', param: 'devisId', sample: 'DEMO' },
      { path: '/devis/paiement-succes/:devisId', label: 'Paiement devis réussi', param: 'devisId', sample: 'DEMO' },
    ],
  },
  {
    title: 'Exploitation (staff)',
    routes: [
      { path: '/dashboard', label: 'Tableau de bord' },
      { path: '/reparations', label: 'Réparations' },
      { path: '/reparations/nouveau', label: 'Nouvelle réparation' },
      { path: '/clients', label: 'Clients' },
      { path: '/stock', label: 'Stock' },
      { path: '/pos', label: 'Caisse (POS)' },
      { path: '/pos-v2', label: 'Caisse v2' },
      { path: '/commandes', label: 'Commandes' },
      { path: '/devis-factures', label: 'Devis & factures' },
      { path: '/avoirs', label: 'Avoirs' },
      { path: '/devis-deplacement', label: 'Devis déplacement' },
      { path: '/calcul', label: 'Calcul devis' },
      { path: '/chat', label: 'Messagerie' },
      { path: '/chat/devis-ia', label: 'Auto-devis (CADBOT)' },
      { path: '/notes', label: 'Notes' },
      { path: '/etiquettes', label: 'Étiquettes' },
      { path: '/notifications', label: 'Notifications' },
      { path: '/historique', label: 'Historique' },
      { path: '/caisse-historique', label: 'Historique caisse' },
      { path: '/ouvrir-caisse', label: 'Ouvrir la caisse' },
      { path: '/fermer-caisse', label: 'Fermer la caisse' },
    ],
  },
  {
    title: 'Administration',
    routes: [
      { path: '/admin/site-settings', label: 'Réglages du site' },
      { path: '/admin/company-settings', label: "Réglages de l'entreprise" },
      { path: '/admin/theme', label: 'Thème' },
      { path: '/admin/users', label: 'Utilisateurs' },
      { path: '/admin/magasins', label: 'Magasins' },
      { path: '/admin/b2b', label: 'B2B' },
      { path: '/admin/marques-modeles', label: 'Marques & modèles' },
      { path: '/admin/cadbot-prix', label: 'CADBOT prix' },
      { path: '/admin/a-faire', label: 'À faire' },
      { path: '/admin/promotions', label: 'Promotions' },
      { path: '/admin/fidelite', label: 'Fidélité' },
      { path: '/admin/translations', label: 'Éditeur de traductions' },
      { path: '/tarifs-gestion', label: 'Gestion des tarifs' },
    ],
  },
  {
    title: 'Super-administration (SaaS)',
    routes: [
      { path: '/super-admin', label: 'Tableau de bord super-admin' },
      { path: '/super-admin/companies', label: 'Entreprises' },
      { path: '/super-admin/users', label: 'Utilisateurs (plateforme)' },
      { path: '/super-admin/subscriptions', label: 'Abonnements' },
      { path: '/super-admin/analytics', label: 'Analytique plateforme' },
      { path: '/super-admin/stats', label: 'Statistiques' },
      { path: '/super-admin/communications', label: 'Communications' },
      { path: '/super-admin/config', label: 'Configuration' },
      { path: '/super-admin/integrations', label: 'Intégrations' },
      { path: '/super-admin/logs', label: 'Journaux' },
      { path: '/super-admin/security', label: 'Sécurité' },
      { path: '/super-admin/packages', label: 'Forfaits' },
      { path: '/super-admin/impersonation', label: 'Impersonation' },
    ],
  },
  {
    title: 'Documents & impression (backend) — pas de lien direct',
    docsOnly: true,
    routes: [
      { path: 'doc:pdf', label: 'PDF devis / factures (7 langues)' },
      { path: 'doc:ticket-papier', label: 'Ticket thermique papier' },
      { path: 'doc:ticket-numerique', label: 'Ticket numérique / PDF (langue client)' },
      { path: 'doc:emails', label: 'E-mails transactionnels' },
    ],
  },
];

const statusOf = (path) => VALIDATION_STATUS[path]?.s || DEFAULT_STATUS;
const noteOf = (path) => VALIDATION_STATUS[path]?.note || '';

const StatusBadge = ({ path }) => {
  const meta = STATUS_META[statusOf(path)] || STATUS_META[DEFAULT_STATUS];
  return (
    <span title={noteOf(path)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 600, color: meta.color, background: meta.bg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.dot }} />
      {meta.label}
    </span>
  );
};

const ValidationTraductionsPage = () => {
  const { t, lang, setLanguage } = useLanguage();
  const [params, setParams] = useState({});

  const { totalRoutes, counts } = useMemo(() => {
    const c = { done: 0, partial: 0, untranslated: 0, review: 0, blocked: 0 };
    let n = 0;
    GROUPS.forEach((g) => g.routes.forEach((r) => { n += 1; c[statusOf(r.path)] = (c[statusOf(r.path)] || 0) + 1; }));
    return { totalRoutes: n, counts: c };
  }, []);

  const buildHref = (route) => {
    let path = route.path;
    if (route.param) {
      const val = (params[route.path] || route.sample || '').trim() || route.sample || 'DEMO';
      path = path.replace(`:${route.param}`, encodeURIComponent(val));
    }
    return path;
  };

  const linkBtn = (route, l) => {
    const href = `${buildHref(route)}?lang=${l.code}`;
    return (
      <a
        key={l.code}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        dir={l.rtl ? 'rtl' : 'ltr'}
        title={`${route.label} — ${l.label}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
          margin: 2, borderRadius: 6, border: '1px solid #d1d5db', background: '#fff',
          fontSize: 12, color: '#111827', textDecoration: 'none', whiteSpace: 'nowrap',
        }}
      >
        <span aria-hidden>{l.flag}</span>
        <span>{l.code}</span>
        <ExternalLink size={11} />
      </a>
    );
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="validation-traductions-page">
        <PageHeader
          title="Validation des traductions"
          subtitle={`${totalRoutes} pages × 7 langues — ouvrez chaque page dans chaque langue pour vérifier`}
          backTo="/dashboard"
          icon={Globe}
          testId="validation-traductions-header"
        />

        {/* Changer la langue de l'application en place (pour naviguer normalement) */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Changer la langue de l'application (navigation en place) — langue active :{' '}
            <span style={{ color: '#2563eb' }}>{lang}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLanguage(l.code)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  borderRadius: 8, cursor: 'pointer', fontSize: 13,
                  border: lang === l.code || (l.code === 'zh-CN' && lang === 'zh') ? '2px solid #2563eb' : '1px solid #d1d5db',
                  background: lang === l.code || (l.code === 'zh-CN' && lang === 'zh') ? '#eff6ff' : '#fff',
                }}
              >
                <span aria-hidden>{l.flag}</span>
                <span>{l.label}</span>
                {(lang === l.code || (l.code === 'zh-CN' && lang === 'zh')) && <Check size={14} color="#2563eb" />}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Astuce : les boutons par ligne ci-dessous ouvrent la page dans un nouvel onglet directement
            dans la langue choisie (lien profond <code>?lang=…</code>, partageable).
          </div>
        </div>

        {/* Tableau de bord honnête : statut réel par page (jamais PASS sur seule présence de clés) */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 10 }}>
            Avancement réel — {totalRoutes} entrées
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <div key={key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px',
                borderRadius: 8, background: meta.bg, color: meta.color, fontSize: 13, fontWeight: 600,
              }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: meta.dot }} />
                {meta.label}
                <span style={{ background: '#fff', borderRadius: 999, padding: '0 7px', fontWeight: 700 }}>
                  {counts[key] || 0}
                </span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>
            Un statut <strong>Traduit et testé</strong> signifie que le rendu visible a été contrôlé
            dans les 7 langues (desktop + mobile, RTL pour ar/ar-SY) — <em>jamais</em> uniquement parce
            que le dictionnaire contient les clés. Table mise à jour après chaque lot.
          </div>
        </div>

        {GROUPS.map((g) => (
          <section key={g.title} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: '#111827', color: '#fff', padding: '10px 14px', fontWeight: 600 }}>
              {g.title} <span style={{ opacity: 0.6, fontWeight: 400 }}>({g.routes.length})</span>
            </div>
            <div>
              {g.routes.map((route, i) => (
                <div
                  key={route.path}
                  style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
                    padding: '10px 14px', borderTop: i ? '1px solid #f3f4f6' : 'none',
                  }}
                >
                  <div style={{ minWidth: 240, flex: '1 1 240px' }}>
                    <div style={{ fontWeight: 500, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {route.label}
                      <StatusBadge path={route.path} />
                    </div>
                    <code style={{ fontSize: 12, color: '#6b7280' }}>{route.path}</code>
                    {noteOf(route.path) && (
                      <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2 }}>{noteOf(route.path)}</div>
                    )}
                  </div>
                  {!g.docsOnly && route.param && (
                    <input
                      value={params[route.path] ?? ''}
                      onChange={(e) => setParams((p) => ({ ...p, [route.path]: e.target.value }))}
                      placeholder={`${route.param} (ex. ${route.sample})`}
                      style={{
                        padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db',
                        fontSize: 12, width: 150,
                      }}
                    />
                  )}
                  {!g.docsOnly && (
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {LANGS.map((l) => linkBtn(route, l))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

        <div style={{ fontSize: 12, color: '#6b7280', paddingBottom: 24 }}>
          Les pages avec identifiant (<code>:ref</code>, <code>:devisId</code>, <code>:shopId</code>)
          nécessitent une vraie valeur pour s'afficher : saisissez-la dans le champ avant d'ouvrir le lien.
          {' '}Les 7 langues cibles : Français, English, Español, Português (BR), العربية, Arabe syrien, 简体中文.
        </div>
      </div>
    </Layout>
  );
};

export default ValidationTraductionsPage;
